import { useEffect, useMemo, useRef, useState } from 'react';
import { formatValueLabel, generateYAxesWithColors } from 'src/utilities';
import { DisplayConfig } from 'src/types/readings/readings';

type UseChartOptionsType = {
  fromDate: Date;
  toDate: Date;
  debouncedExtendTimeframeOnZoomOutOrPan: (min: string, max: string) => void;
  tags: {}[];
  isMobile: boolean;
  displayByTagIndex?: Record<number, DisplayConfig | null>;
  dataSeriesByTagIndex?: Record<number, number[][]>;
  seriesTagIndexes?: number[];
};

export const useChartOptions = ({
  fromDate,
  toDate,
  tags,
  isMobile,
  debouncedExtendTimeframeOnZoomOutOrPan,
  displayByTagIndex,
  dataSeriesByTagIndex,
  seriesTagIndexes,
}: UseChartOptionsType) => {
  const [isPanAndZoomActive, setIsPanAndZoomActive] = useState<boolean>(true);

  const chartRef: any = useRef(null);

  // Held in a ref so the tooltip callback below can stay defined once in the initial
  // options: it is invoked on hover, by which point the ref holds the latest config.
  // Rebuilding `options` whenever new data arrives would churn the whole Chart.js config.
  const labellingRef = useRef({ displayByTagIndex, seriesTagIndexes });
  useEffect(() => {
    labellingRef.current = { displayByTagIndex, seriesTagIndexes };
  }, [displayByTagIndex, seriesTagIndexes]);

  useEffect(() => {
    if (!isMobile) return;
    const chart = chartRef.current;
    const handlePinchMove = (event: any) => {
      if (event.touches.length === 2) {
        const deltaX = Math.abs(event.touches[0].pageX - event.touches[1].pageX);
        const deltaY = Math.abs(event.touches[0].pageY - event.touches[1].pageY);
        if (deltaX > deltaY) {
          chart.options.plugins.zoom.zoom.mode = 'x';
        } else {
          chart.options.plugins.zoom.zoom.mode = 'y';
        }
        chart.update('none');
      }
    };
    window.addEventListener('touchmove', handlePinchMove);
    return () => {
      window.removeEventListener('touchmove', handlePinchMove);
    };
  }, [isMobile]);

  const yAxesWithColors = useMemo(
    () => generateYAxesWithColors(tags, displayByTagIndex, dataSeriesByTagIndex),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags, displayByTagIndex, dataSeriesByTagIndex]
  );
  useEffect(() => {
    setOptions((prevOptions: any) => {
      const newScales = Object.keys(prevOptions.scales || {})
        .filter((key) => !key.startsWith('y'))
        .reduce((acc: Record<string, any>, key: string) => {
          acc[key] = prevOptions.scales[key];
          return acc;
        }, {});

      return {
        ...prevOptions,
        scales: {
          ...newScales,
          ...yAxesWithColors,
          x: {
            ...(newScales.x || {}),
            min: fromDate.toISOString(),
            max: toDate.toISOString(),
          },
        },
      } as any;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yAxesWithColors, fromDate, toDate]);

  const changeTimeFormatHandler = (from: Date, to: Date) => {
    const chartInstance = chartRef?.current;

    setOptions((prevOptions) => {
      const newOptions = {
        ...prevOptions,
        scales: {
          x: {
            type: 'time',
            min: from.toISOString(),
            max: to.toISOString(),
            ticks: {
              maxTicksLimit: 12,
              source: 'auto',
            },
            time: {
              displayFormats: {
                day: 'MMM dd, yyyy h:mm a',
                hour: 'MMM dd, yyyy h:mm a',
              },
            },
          },
        },
      };
      chartInstance.options = newOptions;
      chartInstance.update();
      return newOptions;
    });
  };

  const [options, setOptions] = useState({
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      duration: 0,
    },

    parsing: false,
    transitions: {
      active: {
        animation: {
          duration: 200,
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
      axis: 'xy',
    },
    plugins: {
      tooltip: {
        callbacks: {
          // Keeps the hovered value in step with the axis: a discrete tag whose axis reads
          // "Hand" must not read "1" here. No dataValues filter — a hovered point is by
          // definition a real reading, so it always gets a label.
          label: (context: any) => {
            const { displayByTagIndex: displays, seriesTagIndexes: indexes } = labellingRef.current;
            const seriesName = context.dataset?.label ?? '';
            const value = context.parsed?.y;

            if (value == null) return seriesName;

            const tagIndex = indexes?.[context.datasetIndex];
            const display = tagIndex == null ? null : displays?.[tagIndex];

            return `${seriesName}: ${formatValueLabel(value, display)}`;
          },
        },
      },
      legend: {
        position: 'top',
        align: 'start',
        display: true,

        labels: {
          boxWidth: 6,
          boxHeight: 7,
          pointStyle: 'circle',
          usePointStyle: true,
          useBorderRadius: true,
          padding: 10,
          lineHeight: 1.2,
        },
      },
      zoom: {
        enabled: true,
        zoomboxBackgroundColor: 'rgba(0, 0, 0, 0.1)',
        zoomboxBorderColor: 'rgba(0, 0, 0, 0.5)',
        zoomButtonText: null,
        zoomButtonClass: null,
        pan: {
          enabled: isPanAndZoomActive,
          mode: 'xy',
          speed: 10,
          onPan: ({ chart }: { chart: any }) => {
            if (chartRef) {
              const { min, max } = chart.scales.x;
              debouncedExtendTimeframeOnZoomOutOrPan(min, max);
            }
          },
        },
        zoom: {
          wheel: {
            enabled: !isMobile && isPanAndZoomActive,
          },
          drag: {
            backgroundColor: 'rgba(99, 115, 129, 0.5)',
            color: 'rgba(99, 115, 129, 0.5)',
            enabled: !isPanAndZoomActive,
          },
          pinch: {
            enabled: isPanAndZoomActive,
            mode: 'xy',
          },

          mode: 'x',
          speed: 10,
          onZoom: ({ chart }: { chart: any }) => {
            const { min, max } = chart.scales.x;
            debouncedExtendTimeframeOnZoomOutOrPan(min, max);
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        min: fromDate.toISOString(),
        max: toDate.toISOString(),
        ticks: {
          maxTicksLimit: 12,
          source: 'auto',
        },
        time: {
          displayFormats: {
            day: 'MMM dd, yyyy h:mm a',
            hour: 'MMM dd, yyyy h:mm a',
          },
        },
      },
    },
  });

  useEffect(() => {
    const chartInstance = chartRef?.current;
    const zoomLevel = chartInstance?.getZoomLevel();
    setOptions((prevOptions) => {
      const newOptions = {
        ...prevOptions,
        plugins: {
          ...prevOptions.plugins,
          zoom: {
            ...prevOptions.plugins.zoom,
            pan: {
              ...prevOptions.plugins.zoom.pan,
              enabled: false,
            },
            zoom: {
              ...prevOptions.plugins.zoom.zoom,
              wheel: {
                ...prevOptions.plugins.zoom.zoom.wheel,
                enabled: false,
              },
              drag: {
                backgroundColor: 'rgba(99, 115, 129, 0.5)',
                color: 'rgba(99, 115, 129, 0.5)',
                enabled: true,
                mode: 'x',
              },
              pinch: {
                ...prevOptions.plugins.zoom.zoom.pinch,
                enabled: false,
              },
              level: zoomLevel,
            },
          },
        },
      };
      chartInstance.options = newOptions;
      chartInstance.update();
      return newOptions;
    });

    setIsPanAndZoomActive(false);
  }, []);

  const togglePanAndZoom = () => {
    const chartInstance = chartRef?.current;
    const zoomLevel = chartInstance?.getZoomLevel();

    setIsPanAndZoomActive((prevState) => {
      const newState = !prevState;

      if (chartRef && chartRef.current) {
        chartRef.current.canvas.style.touchAction = newState ? 'none' : 'unset';
      }

      return newState;
    });

    setOptions((prevOptions) => {
      const newOptions = {
        ...prevOptions,
        plugins: {
          ...prevOptions.plugins,
          zoom: {
            ...prevOptions.plugins.zoom,
            pan: {
              ...prevOptions.plugins.zoom.pan,
              enabled: !prevOptions.plugins.zoom.pan.enabled,
            },
            zoom: {
              ...prevOptions.plugins.zoom.zoom,
              wheel: {
                ...prevOptions.plugins.zoom.zoom.wheel,
                enabled: !prevOptions.plugins.zoom.zoom.wheel.enabled,
              },
              drag: {
                ...prevOptions.plugins.zoom.pan,
                enabled: !prevOptions.plugins.zoom.zoom.drag.enabled,
                backgroundColor: 'rgba(99, 115, 129, 0.5)',
                color: 'rgba(99, 115, 129, 0.5)',
              },
              pinch: {
                ...prevOptions.plugins.zoom.zoom.pinch,
                enabled: !prevOptions.plugins.zoom.zoom.pinch.enabled,
              },
              level: zoomLevel,
            },
          },
        },
      };
      chartInstance.options = newOptions;
      chartInstance.update();
      return newOptions;
    });
  };

  const onResetZoomHandler = () => {
    if (chartRef) {
      chartRef.current.resetZoom();
    }
  };

  const zoomInHandler = () => {
    if (chartRef) {
      chartRef.current.zoom(1.2);
    }
  };

  const zoomOutHandler = () => {
    if (chartRef) {
      chartRef.current.zoom(0.8);
    }
  };

  return {
    chartRef,
    onResetZoomHandler,
    zoomInHandler,
    zoomOutHandler,
    isPanAndZoomActive,
    togglePanAndZoom,
    options,
    changeTimeFormatHandler,
  };
};
